import {
  createRootRouteWithContext,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import * as React from 'react';
import { ToastContainer } from 'react-toastify';

import type { RouterContext } from '@/lib/router';

import 'react-toastify/dist/ReactToastify.css';

const TanStackRouterDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        }))
      );

const TanStackQueryDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/react-query-devtools').then((res) => ({
          default: res.ReactQueryDevtools,
        }))
      );

const RootComponent = () => {
  const location = useLocation({ select: (path) => path.pathname });
  const posthog = usePostHog();

  // biome-ignore lint/correctness/useExhaustiveDependencies: should only run when location changes
  React.useEffect(() => {
    posthog.capture('$pageview');
  }, [location]);

  return (
    <RootDocument>
      <Outlet />
      <TanStackRouterDevtools />
      <TanStackQueryDevtools />
      <ToastContainer position="bottom-right" />
    </RootDocument>
  );
};

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'TanStack Start Starter',
      },
    ],
  }),
  component: RootComponent,
});

const RootDocument = ({ children }: { children: React.ReactNode }) => {
  return (
    <React.Fragment>
      {children}
      <ScrollRestoration />
      <Scripts />
    </React.Fragment>
  );
};
